import React, { useEffect, useState, useContext } from 'react'; // useContext 복구
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';

import {
    Heart,
    HeartOff,
    Share2,
    Info,
    ShoppingBag,
    RotateCcw
  } from "lucide-react";

import { useAuth } from '../context/AuthContext'; 
import { useCart } from '../context/CartContext';
import { useShop } from '../context/ShopContext';

import RefundPolicyPage from './RefundPolicyPage';
import ProductFooter from '../components/ProductFooter';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { user } = useAuth();
    const { fetchCartCount } = useCart();
    const { setShopId } = useShop();

    const [product, setProduct] = useState(null);
    const [images, setImages] = useState([]);
    const [qty, setQty] = useState(1);
    const [activeTab, setActiveTab] = useState('tab-desc');
    const [stats, setStats] = useState({ avg: 0, cnt: 0 });
    const [isWished, setIsWished] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSlideOpen, setIsSlideOpen] = useState(false);
    const [slideAction, setSlideAction] = useState('cart');
    const [showShippingInfo, setShowShippingInfo] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(1);

    // 추가 데이터 상태
    const [reviews, setReviews] = useState([]);
    const [qnaList, setQnaList] = useState([]);
    const [question, setQuestion] = useState('');
    const [isSecret, setIsSecret] = useState(false);

    // 상품 옵션
    const [optionGroups, setOptionGroups] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState({});
    const clearOption = (groupId) => {
        setSelectedOptions(prev => {
            const next = { ...prev };
            delete next[groupId];
            return next;
        });
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        axios.get(`/api/product/${id}`)
            .then(res => {
                setProduct(res.data.product);
                setImages(res.data.images || []);
                setStats(res.data.stats || { avg: 0, cnt: 0 });
                setIsWished(!!res.data.isWished);
                setReviews(res.data.reviews || []);
                setQnaList(res.data.qna_list || []);
                if (res.data?.product?.shop_id) {
                    setShopId(String(res.data.product.shop_id));
                }
            })
            .catch(err => console.error("상품 로드 실패:", err));
    }, [id, setShopId]);

    useEffect(() => {
        axios.get(`/api/products/${id}/options`)
            .then(res => {
                if (res.data.success) {
                    setOptionGroups(res.data.data || []);
                }
            })
            .catch(err => {
                console.error(err);
            });
    }, [id]);

    if (!product) return <div style={{ padding: '100px', textAlign: 'center' }}>로딩 중...</div>;

    const changeQty = (n) => setQty(prev => Math.max(1, prev + n));

    const submitOrder = async (action) => {
        if (!user) {
            alert("로그인이 필요합니다.");
            navigate("/login", {
                state: { from: window.location.pathname }
            });
            return;
        }
    
        const missingRequired = optionGroups.some(group =>
            group.required === 'Y' && !selectedOptions[group.group_id]
        );
    
        if (missingRequired) {
            alert('필수 옵션을 선택해주세요.');
            return;
        }
    
        try {
            if (action === 'buy') {
                navigate('/order/now', {
                    state: {
                        product: {
                            id: product.id,
                            name: product.name,
                            quantity: qty,
                            price: product.price,
    
                            selectedOptions,
                            selectedOptionItems,
                            optionPrice
                        }
                    }
                });
                return;
            }
    
            const res = await axios.post(
                '/api/cart/add',
                {
                    product_id: product.id,
                    quantity: qty,
                    action,
                    selected_options: selectedOptions,
                    selected_option_items: selectedOptionItems,
                    option_price: optionPrice
                },
                { withCredentials: true }
            );
    
            if (res.data?.success) {
                await fetchCartCount();
                alert('장바구니에 담겼습니다.');
                navigate('/cart');
            } else {
                alert(res.data?.message || '처리 중 오류가 발생했습니다.');
            }
    
        } catch (err) {
            console.error(err);
    
            if (err.response?.status === 401) {
                alert("로그인이 필요합니다.");
                navigate("/login", {
                    state: { from: window.location.pathname }
                });
            } else {
                alert("서버 오류가 발생했습니다.");
            }
        } finally {
            setIsSlideOpen(false);
        }
    };

    const handleQnaSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post('/api/product/qna/save', {
                product_id: product.id,
                question: question,
                is_secret: isSecret ? 1 : 0
            });
            if (res.data.success) {
                alert('질문이 등록되었습니다.');
                setQuestion('');
                setIsSecret(false);
                // 새로고침 로직
                window.location.reload();
            }
        } catch (err) {
            alert('질문 등록 중 오류가 발생했습니다.');
        }
    };

    const toggleWish = async () => {
        const prev = isWished;

        setIsWished(!prev);

        try {
            const res = await axios.post(
                '/api/product/wishlist/toggle',
                { product_id: product.id },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                }
            );

            const data = res.data || {};

            if (!data.success) {
                setIsWished(prev);
                alert(data.message || '처리 실패');
                return;
            }

            setIsWished(data.action === 'added');
        } catch (err) {
            setIsWished(prev);

            if (err.response?.status === 401) {
                alert('로그인이 필요합니다.');
                navigate('/login');
                return;
            }

            alert(err.response?.data?.message || '통신 오류');
        }
    };

    const handleShare = async () => {
        try {
            await navigator.share({
                title: product.name,
                text: product.name,
                url: window.location.href
            });
        } catch (err) {
            console.log('공유 취소');
        }
    };

    const handleOptionChange = (groupId, itemId) => {
        setSelectedOptions(prev => ({
            ...prev,
            [groupId]: itemId
        }));
    };

    const optionPrice = optionGroups.reduce((sum, group) => {
        const itemId = selectedOptions[group.group_id];
    
        if (!itemId) return sum;
    
        const item = group.items.find(i => i.item_id === itemId);
    
        return sum + (item ? Number(item.extra_price) : 0);
    }, 0);

    const selectedOptionItems = optionGroups
    .map(group => {
      const itemId = selectedOptions[group.group_id];
      const item = group.items.find(i => Number(i.item_id) === Number(itemId));
  
      if (!item) return null;
  
      return {
        group_id: group.group_id,
        group_name: group.group_name,
        item_id: item.item_id,
        item_name: item.item_name,
        extra_price: Number(item.extra_price || 0)
      };
    })
    .filter(Boolean);

    return (
        <div style={isMobile ? styles.mobileContainer : styles.container}>
            <style>{`
                .swiper-pagination-bullet {
                    background-color: rgba(255, 255, 255, 0.4) !important;
                }
                .swiper-pagination-bullet-active {
                    background-color: rgba(255, 255, 255, 0.8) !important;
                }
                .swiper-button-next,
                .swiper-button-prev {
                    color: rgba(255, 255, 255, 0.7) !important;
                }
                .swiper-button-next::after,
                .swiper-button-prev::after {
                    color: rgba(255, 255, 255, 0.7) !important;
                    font-size: 20px !important;
                }
            `}</style>

            <section style={isMobile ? styles.mobileProductBox : styles.productBox}>
                {/* 이미지 영역 */}
                <div style={isMobile ? styles.mobileProductImage : styles.productImage}>
                    <div style={styles.swiperWrap}>
                        <Swiper
                            modules={[Navigation, Pagination]}
                            loop={true}
                            navigation={!isMobile}
                            pagination={{ clickable: true }}
                            style={{ width: '100%', height: '100%' }}
                            onSlideChange={(swiper) => setCurrentImageIndex(swiper.realIndex + 1)}
                            onSwiper={(swiper) => setCurrentImageIndex(swiper.realIndex + 1)}
                        >
                            {images.map((img, idx) => (
                                <SwiperSlide key={idx}>
                                    <img src={`/static/${img}`} alt="상품" style={styles.imgFluid} />
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        {isMobile && images.length > 0 && (
                            <div style={styles.imgCountOverlay}>
                                <span>{currentImageIndex}</span> / <span>{images.length}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 정보 영역 */}
                <div style={isMobile ? styles.mobileProductInfo : styles.productInfo}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={styles.productTitle}>{product.name}</h2>
                        <div style={{ display: 'flex', gap: '15px' }}>
                        <button
                            onClick={toggleWish}
                            style={styles.iconBtn}
                            type="button"
                        >
                            <Heart
                                size={20}
                                strokeWidth={1.8}
                                fill={isWished ? "#d32f2f" : "none"}
                                color={isWished ? "#d32f2f" : "#aaa"}
                            />
                        </button>
                        
                        <button
                            onClick={handleShare}
                            style={styles.iconBtn}
                            type="button"
                        >
                            <Share2
                                size={19}
                                strokeWidth={1.8}
                                color="#aaa"
                            />
                        </button>

                        </div>
                    </div>

                    <div style={styles.shortDescription} dangerouslySetInnerHTML={{ __html: product.short_description }} />

                    <div style={styles.ratingReview}>
                        <span style={{ color: '#555' }}>★</span>
                        <span style={{ fontWeight: 600 }}>{Number(stats.avg).toFixed(1)}</span> | 
                        <span style={styles.reviewLink} onClick={() => {
                            setActiveTab('tab-review');
                            document.getElementById('tabMenu').scrollIntoView();
                        }}> 리뷰 {stats.cnt}개 보기</span>
                    </div>

                    <div style={styles.price}>
                    {
                        product.sale_price > 0 &&
                        product.price < product.original_price ? (
                        <>
                            <div style={{ textDecoration: 'line-through', marginTop: '30px', fontSize: '16px', color: '#999' }}>
                                {product.original_price.toLocaleString()}원
                            </div>

                            <div>
                            <span style={{ fontSize: '25px', fontWeight: 700 }}>
                                {product.price.toLocaleString()}원
                            </span>

                            <span
                                style={{
                                color: '#999',
                                fontSize: '16px',
                                marginLeft: '6px',
                                fontWeight: 500
                                }}
                            >
                                {Math.round(
                                ((product.original_price - product.price) /
                                    product.original_price) *
                                    100
                                )}
                                %
                            </span>
                            </div>
                        </>
                        ) : (
                        <>
                            {product.price.toLocaleString()}원
                        </>
                        )
                    }
                    </div>

                    <div style={{ fontSize: '0.9rem', marginTop: '20px', marginBottom: '0px', position: 'relative' }}>
                    <span style={{ color: '#6c757d' }}>
                        배송비: 조건별배송{' '}
                        <Info
                            size={16}
                            strokeWidth={2}
                            color="#0d6efd"
                            style={{
                                cursor:'pointer',
                                verticalAlign:'middle'
                            }}
                            onClick={() => setShowShippingInfo(prev => !prev)}
                        />
                    </span>

                        {showShippingInfo && (
                            <div style={styles.miniInfoBox}>
                                <strong style={{ color: '#212529' }}>배송 조건 안내</strong>
                                <hr style={{ margin: '8px 0' }} />
                                <div>• 매장픽업은 배송료없음</div>
                                <div>• 3만원 이상 무료배송</div>
                                <div>• 도서산간 지역 추가 운임 발생</div>
                                <hr style={{ margin: '8px 0' }} />
                            </div>
                        )}
                    </div>

                    {!isMobile && <hr className="my-2" />}

                    {/* PC 전용 컨트롤 */}
                    {!isMobile && (
                        <div id="pcControls">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px' }}>
                                <small style={{ color: '#6c757d', fontWeight: 700 }}>제품명: {product.name}</small>
                                <div style={styles.quantitySelector}>
                                    <button style={styles.qtyBtn} onClick={() => changeQty(-1)}>−</button>
                                    <input style={styles.qtyInput} value={qty} readOnly />
                                    <button style={styles.qtyBtn} onClick={() => changeQty(1)}>+</button>
                                </div>
                            </div>
                            <hr className="my-3" />

                            {optionGroups.length > 0 && (
                            <div style={{marginTop:'20px'}}>

                                {optionGroups.map(group => (

                                    <div key={group.group_id} style={{marginBottom:'15px'}}>

                                        <div
                                            style={{
                                                marginBottom:'6px',
                                                fontWeight:'bold'
                                            }}
                                        >
                                            {group.group_name}

                                            {group.required === 'Y' && (
                                                <span style={{color:'red'}}> *</span>
                                            )}
                                        </div>

                                        <select
                                            value={selectedOptions[group.group_id] || ''}
                                            onChange={(e)=>handleOptionChange(
                                                group.group_id,
                                                Number(e.target.value)
                                            )}
                                            style={{
                                                width: '100%',
                                                padding: '6px 8px',
                                                fontSize: '13px',
                                                border: '1px solid #ddd',
                                                borderRadius: '0px'
                                            }}
                                        >

                                            <option value="">선택하세요</option>

                                            {group.items.map(item=>(
                                                <option
                                                    key={item.item_id}
                                                    value={item.item_id}
                                                >
                                                    {item.item_name}

                                                    {item.extra_price>0 &&
                                                        ` (+${item.extra_price.toLocaleString()}원)`
                                                    }

                                                </option>
                                            ))}

                                        </select>

                                    </div>

                                ))}

                            </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>총합계:</strong>
                                <div style={{ ...styles.price, fontSize: '1.2rem', margin: 0 }}>{((product.price * qty) + (optionPrice * qty)).toLocaleString()}원</div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button style={styles.btnCart} onClick={() => submitOrder('cart')}>장바구니</button>
                                <button style={styles.btnBuy} onClick={() => submitOrder('buy')}>구매하기</button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* 탭 메뉴 */}


            <div style={isMobile ? styles.mobileTabContent : styles.tabContent}>
                <div style={styles.tabWrap}>
                    <ul style={isMobile ? styles.mobileTabMenu : styles.tabMenu} id="tabMenu">
                        <li
                            style={
                                isMobile
                                    ? (activeTab === 'tab-desc'
                                        ? styles.mobileTabActive
                                        : styles.mobileTab)
                                    : (activeTab === 'tab-desc'
                                        ? styles.tabActive
                                        : styles.tab)
                            }
                            onClick={() => setActiveTab('tab-desc')}
                        >
                            <span style={activeTab === 'tab-desc' ? styles.tabTextActive : styles.tabText}>
                                상세설명
                            </span>
                        </li>
                        <li
                            style={
                                isMobile
                                    ? (activeTab === 'tab-refund'
                                        ? styles.mobileTabActive
                                        : styles.mobileTab)
                                    : (activeTab === 'tab-refund'
                                        ? styles.tabActive
                                        : styles.tab)
                            }
                            onClick={() => setActiveTab('tab-refund')}
                        >
                            <span style={activeTab === 'tab-refund' ? styles.tabTextActive : styles.tabText}>
                                교환환불
                            </span>
                        </li>
                        <li
                            style={
                                isMobile
                                    ? (activeTab === 'tab-review'
                                        ? styles.mobileTabActive
                                        : styles.mobileTab)
                                    : (activeTab === 'tab-review'
                                        ? styles.tabActive
                                        : styles.tab)
                            }
                            onClick={() => setActiveTab('tab-review')}
                        >
                            <span style={activeTab === 'tab-review' ? styles.tabTextActive : styles.tabText}>
                                리뷰({reviews.length})
                            </span>
                        </li>
                        <li
                            style={
                                isMobile
                                    ? (activeTab === 'tab-qna'
                                        ? styles.mobileTabActive
                                        : styles.mobileTab)
                                    : (activeTab === 'tab-qna'
                                        ? styles.tabActive
                                        : styles.tab)
                            }
                            onClick={() => setActiveTab('tab-qna')}
                        >
                            <span style={activeTab === 'tab-qna' ? styles.tabTextActive : styles.tabText}>
                                Q&A({qnaList.length})
                            </span>
                        </li>
                    </ul>
                </div>


                {activeTab === 'tab-desc' && <div style={{textAlign: 'left'}} dangerouslySetInnerHTML={{ __html: product.description }} />}
                
                {activeTab === 'tab-refund' && (
                    <RefundPolicyPage />
                )}

                {activeTab === 'tab-review' && (
                    <div style={{ textAlign: 'left' }}>
                        {reviews.length > 0 ? reviews.map((r, i) => (
                        <div key={i} style={{ borderBottom: '1px solid #eee', padding: '20px 0' }}>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{r.nickname}</strong>
                            <small style={{ color: '#999' }}>
                                {new Date(r.created_at).toLocaleDateString()}
                            </small>
                            </div>

                            {/* 별점 */}
                            <div style={{ color: '#ffc107', margin: '5px 0' }}>
                            {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                            </div>

                            {/* 리뷰 내용 */}
                            <div
                            style={{ color: '#666', fontSize: '0.9rem' }}
                            dangerouslySetInnerHTML={{ __html: r.content }}
                            />

                            {/* ✅ 리뷰 이미지 출력 */}
                            {r.image_path && (
                            <div style={{ marginTop: '10px' }}>
                                <img
                                src={`/static/${r.image_path.replace(/^.*static\//, '')}`}
                                alt="리뷰 이미지"
                                style={{
                                    maxWidth: '120px',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => window.open(`/static/${r.image_path}`, '_blank')}
                                />
                            </div>
                            )}
                        </div>
                        )) : (
                        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                            등록된 리뷰가 없습니다.
                        </div>
                        )}
                    </div>
                )}

                {activeTab === 'tab-qna' && (
                    <div style={{textAlign: 'left'}}>
                        <form onSubmit={handleQnaSubmit} style={{ marginBottom: '30px', padding: '20px', background: '#f9f9f9' }}>
                            <textarea className="form-control" rows="3" placeholder="문의 내용을 입력하세요" value={question} onChange={(e) => setQuestion(e.target.value)} required />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                                <label style={{ fontSize: '0.85rem' }}>
                                    <input type="checkbox" checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} /> 비밀글로 등록
                                </label>
                                <button type="submit" className="btn btn-dark btn-sm">질문 등록</button>
                            </div>
                        </form>

                        {qnaList.length > 0 ? qnaList.map((q, i) => {
                            // 2. 권한 체크 로직 (원본 Flask 로직 100% 복구)
                            const isOwner = user && String(user.id) === String(q.user_id);
                            const isAdmin = user && (user.role === 'admin' || user.id === 'admin');
                            const canView = !q.is_secret || isOwner || isAdmin;

                            return (
                                <div key={i} style={{ borderTop: '1px solid #eee', padding: '15px 0' }}>
                                    {!canView ? (
                                        <div style={{ color: '#999', fontSize: '0.85rem' }}>🔒 비밀글입니다. 작성자와 관리자만 볼 수 있습니다.</div>
                                    ) : (
                                        <>
                                            <div style={{ fontWeight: 'bold' }}>{q.is_secret ? '🔒 ' : ''} {q.question}</div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
                                                <span>{q.nickname} · {new Date(q.created_at).toLocaleDateString()}</span>
                                                <span style={{ color: q.status === 'answered' ? '#0073e9' : '#999' }}>
                                                    {q.status === 'answered' ? '답변완료' : '대기중'}
                                                </span>
                                            </div>
                                            {q.status === 'answered' && (
                                                <div style={{ marginTop: '10px', padding: '15px', background: '#f1f3f5', borderRadius: '4px', fontSize: '0.85rem' }}>
                                                    <strong>관리자 답변:</strong>
                                                    <div style={{ marginTop: '5px' }} dangerouslySetInnerHTML={{ __html: q.answer }} />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        }) : <div style={{textAlign: 'center', padding: '50px', color: '#999'}}>문의 내역이 없습니다.</div>}
                    </div>
                )}

                {/* 안내선 */}
                <div
                    style={{
                        borderTop: '0px solid #e9ecef',
                        marginTop: '30px',
                        marginBottom: '30px'
                    }}
                >
                </div>
                <ProductFooter />

            </div>


            {/* 모바일 하단 고정 바 */}
            {isMobile && (
                <>
                    <div style={styles.bottomFixed}>
                        <button
                            style={styles.btnCartMobile}
                            onClick={() => { setSlideAction('cart'); setIsSlideOpen(true); }}
                        >
                            <i className="bi bi-bag" style={{ fontSize: '24px' }}></i>
                        </button>

                        <button
                            style={styles.btnBuyMobile}
                            onClick={() => { setSlideAction('buy'); setIsSlideOpen(true); }}
                        >
                            구매하기
                        </button>
                    </div>

                    {/* 오버레이 슬라이드 패널 */}
                    <div style={{ ...styles.overlay, ...(isSlideOpen ? styles.overlayActive : {}) }} onClick={() => setIsSlideOpen(false)} />

                    <div style={{ ...styles.slidePanel, ...(isSlideOpen ? styles.slidePanelShow : {}) }}>
                        <div style={styles.slideHeader}>
                            <span style={styles.slideTitle}>{product.name}</span>
                            <button
                                type="button"
                                style={styles.slideCloseBtn}
                                onClick={() => setIsSlideOpen(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div style={styles.slideContent}>

                                {optionGroups.length > 0 && (
                                <div style={styles.optionSection}>
                                    {optionGroups.map((group, idx) => (
                                    <div
                                        key={group.group_id}
                                        style={{
                                        ...styles.optionGroup,
                                        ...(idx === optionGroups.length - 1
                                            ? { borderBottom: 'none', paddingBottom: 0 }
                                            : {})
                                        }}
                                    >
                                        <div style={styles.optionGroupTitle}>
                                        <span>{group.group_name}</span>
                                        {group.required === 'Y' && (
                                            <span style={styles.requiredMark}>필수</span>
                                        )}
                                            <button
                                                type="button"
                                                onClick={() => clearOption(group.group_id)}
                                                style={styles.clearOptionBtn}
                                            >
                                                <RotateCcw size={14} />
                                            </button>
                                        </div>

                                        <div style={styles.optionCardList}>
                                        {group.items.map(item => {
                                            const selected =
                                            Number(selectedOptions[group.group_id]) === Number(item.item_id);

                                            return (
                                            <button
                                                key={item.item_id}
                                                type="button"
                                                onClick={() => handleOptionChange(group.group_id, Number(item.item_id))}
                                                style={{
                                                ...styles.optionCard,
                                                ...(selected ? styles.optionCardSelected : {})
                                                }}
                                            >
                                                <span>{item.item_name}</span>

                                                {Number(item.extra_price || 0) > 0 && (
                                                <span style={styles.optionExtraPrice}>
                                                    +{Number(item.extra_price).toLocaleString()}원
                                                </span>
                                                )}
                                            </button>
                                            );
                                        })}
                                        </div>
                                    </div>
                                    ))}
                                </div>
                                )}

                            </div>

                            <div
                                style={{
                                    ...styles.slideFooter,
                                    ...(optionGroups.length > 0
                                        ? { borderTop: 'none' }
                                        : {})
                                }}
                            >
                                <div style={styles.slideTotalRow}>
                                    <div style={styles.slideStepper}>
                                        <button style={styles.slideQtyBtn} onClick={() => changeQty(-1)}>−</button>
                                        <div style={styles.slideQtyValueWrap}>
                                        <input style={styles.slideQtyInput} value={qty} readOnly />
                                        </div>
                                        <button style={styles.slideQtyBtn} onClick={() => changeQty(1)}>+</button>
                                    </div>
                                    <span style={styles.slideTotalPrice}>
                                    {((product.price + optionPrice) * qty).toLocaleString()}원
                                    </span>
                                </div>

                                <button
                                    style={
                                        slideAction === 'cart'
                                            ? styles.btnBuyButton
                                            : styles.btnBuyButton
                                    }
                                    onClick={() => submitOrder(slideAction)}
                                >
                                    {slideAction === 'cart' ? (
                                        '장바구니'
                                    ) : (
                                        '구매하기'
                                    )}
                                </button>
                            </div>

                    </div>
                </>
            )}
        </div>
    );
};

// 기존 스타일 유지
const styles = {
    container: { maxWidth: '1200px', margin: '20px auto 0px', padding: '0 20px' },
    mobileContainer: { width: '100%', marginTop: '0px', marginBottom: '90px' },
    productBox: {
        display: 'grid',
        gridTemplateColumns: '500px 1fr',
        gap: '50px',
        background: '#fff',
        padding: '40px',
        boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
        alignItems: 'stretch'
      },
    mobileProductBox: { display: 'block', background: '#fff' },
    productImage: {
        width: '100%',
        height: '100%',
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: '#fff'
      },
    mobileProductImage: { width: '100%', height: 'auto', minHeight: '0' },
    swiperWrap: {
        width: '100%',
        height: '100%',
        position: 'relative'
    },
    imgFluid: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      },
    imgCountOverlay: {
        position: 'absolute',
        bottom: '6px',
        right: '6px',
        background: 'rgba(0,0,0,0.45)',
        color: '#fff',
        fontSize: '12px',
        padding: '2px 6px',
        borderRadius: '10px',
        zIndex: 5
    },
    productInfo: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
    mobileProductInfo: { padding: '30px 15px' },
    productTitle: { fontSize: '1.5rem', margin: 0, fontWeight: 700 },
    shortDescription: { margin: '20px 0', fontSize: '1rem', color: '#444', lineHeight: '1.5' },
    ratingReview: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#555' },
    reviewLink: { cursor: 'pointer', fontWeight: 600, textDecoration: 'none' },
    price: { fontSize: '1.5rem', fontWeight: 700, marginTop: '20px' },
    quantitySelector: { display: 'inline-flex', border: '1px solid #ccc', background: '#fff' },
    qtyBtn: { width: '40px', height: '35px', border: 'none', background: '#fff', fontSize: '20px', cursor: 'pointer' },
    qtyInput: { width: '50px', textAlign: 'center', border: 'none', fontWeight: 'bold' },
    btnCart: { flex: 1, height: '60px', background: '#f5f5f5', border: 'none', fontWeight: 'bold', cursor: 'pointer' },
    btnBuy: { flex: 1, height: '60px', background: '#007bff', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' },
    tabWrap: {
        borderBottom: '2px solid #e9ecef',
        marginBottom: '30px'
      },
    tabMenu: {
        display: 'flex',
        justifyContent: 'space-between',
        listStyle: 'none',
        padding: 0,
        margin: '40px 20px 0'
    },
    tab: {
        padding: '12px 20px 0 16px',
        cursor: 'pointer',
        color: '#6c757d',
        position: 'relative',
        zIndex: 1
    },
    tabActive: {
        padding: '12px 20px 0 16px',
        cursor: 'pointer',
        color: '#0073e9',
        fontWeight: '700',
        position: 'relative',
        zIndex: 1
    },
    tabText: {
        display: 'inline-block',
        paddingBottom: '4px',
        borderBottom: '2px solid transparent',
        fontWeight: 700
    },
    tabTextActive: {
        display: 'inline-block',
        paddingBottom: '4px',
        borderBottom: '3px solid #0073e9',
        marginBottom: '-1px',
        fontWeight: 700
    },

    tabContent: { background: '#fff', padding: '0px 40px', boxShadow: 'none', color: '#666', lineHeight: '1.8' },
    iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 0 },

    miniInfoBox: {
        display: 'block',
        position: 'absolute',
        zIndex: 1055,
        width: '250px',
        fontSize: '0.75rem',
        color: '#6c757d',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '0.375rem',
        padding: '8px',
        marginTop: '8px',
        boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.075)'
    },

    bottomFixed: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '58px',
        padding: '4px 20px 0 15px',
        display: 'flex',
        gap: '10px',
        zIndex: 1000,
        background: '#fff',
        borderTop: 'none'
    },

    btnCartMobile: {
        width: '15%',
        background: '#ffffff',
        border: '0px solid #d0d0d0',
        color: '#444',
        fontSize: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        height: '50px',
        cursor: 'pointer'
    },

    btnBuyMobile: {
        width: '85%',
        background: '#007bff',
        border: 'none',
        color: '#ffffff',
        borderRadius: '30px',
        height: '50px',
        fontSize: '15px',
        fontWeight: 500,
        cursor: 'pointer'
    },

    btnBuyButton: {
        width: '100%',
        background: '#007bff',
        border: 'none',
        color: '#ffffff',
        borderRadius: '30px',
        height: '50px',
        fontSize: '15px',
        fontWeight: 500,
        cursor: 'pointer'
    },

    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', opacity: 0, visibility: 'hidden', transition: 'opacity 0.3s', zIndex: 1001 },
    overlayActive: { opacity: 1, visibility: 'visible' },

    slidePanel: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
      
        transform: 'translateY(100%)',
        transition: 'transform .3s ease',
      
        background: '#fff',
        padding: '20px',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        zIndex: 1050,
        maxHeight: '70vh',
      
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    
    slideContent: {
        flex: 1,
        overflowY: 'auto',
        paddingRight: '2px',

        marginLeft: '-20px',
        marginRight: '-20px'
    },
      
      slideFooter: {
        flexShrink: 0,
        paddingTop: '14px',
        background: '#fff',
        borderTop: '1px solid #eee'
    },

    slidePanelShow: {
        transform: 'translateY(0)'
    },

    slideHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
    },

    optionDivider: {
        height: '1px',
        background: '#eee',
        margin: '0',
        flexShrink: 0
    },

    optionSection: {
        marginTop: '-12px',
    
        padding: '12px 20px',
    
        background: '#f8f8f8'
    },

    slideTitle: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#000'
    },

    slideCloseBtn: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        color: '#000',
        cursor: 'pointer',
        lineHeight: 1
    },

    slideQtyBox: {
        background: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px'
    },

    slideQtyRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    slideProductName: {
        fontSize: '14px',
        color: '#333',
        fontWeight: 500,
        flex: 1,
        marginRight: '15px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },

    slideStepper: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    
    slideQtyBtn: {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        border: '1px solid #d9d9d9',
        background: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '20px',
        fontWeight: '600',
        color: '#555',
        cursor: 'pointer',
        padding: 0,
        lineHeight: 1,
        transition: 'all .15s ease'
    },
    
    slideQtyValueWrap: {
        minWidth: '36px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    
    slideQtyInput: {
        width: '32px',
        border: 'none',
        background: 'transparent',
        textAlign: 'center',
        fontSize: '18px',
        fontWeight: '600',
        color: '#222',
        outline: 'none'
    },

    slideTotalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        marginTop: '4px'
    },

    slideTotalLabel: {
        color: '#666',
        fontSize: '14px'
    },

    slideTotalPrice: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#000'
    },

    btnConfirm: {
        width: '100%',
        padding: '18px',
        background: '#007bff',
        color: '#fff',
        border: 'none',
        fontWeight: 'bold',
        fontSize: '16px',
        borderRadius: '5px',
        cursor: 'pointer'
    },
    mobileTabMenu: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        listStyle: 'none',
        padding: '0',
        margin: '0px 0 0'
    },
    mobileTab: {

        textAlign: 'center',
        padding: '10px 0px 0px',
        cursor: 'pointer'
    },
    
    mobileTabActive: {

        textAlign: 'center',
        padding: '10px 0px 0px',
        cursor: 'pointer',
        color: '#0073e9',
        fontWeight: 700
    },
    mobileTabContent: {
        background: '#fff',
        color: '#666',
        lineHeight: '1.8',
        padding: '0px 15px',
        boxShadow: 'none'
    },
      
    mobileOptionSection: {
    marginTop: '16px',
    },
    
    optionGroup: {
        marginTop: '18px',
        marginBottom: '18px',
        paddingBottom: '18px',
        borderBottom: '1px solid #e0e0e0',

    },
    
    optionGroupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
    fontSize: '16px',
    fontWeight: 700,
    color: '#222',
    },
    
    requiredMark: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    padding: '0 6px',
    borderRadius: '9px',
    background: '#eef4ff',
    color: '#0073e9',
    fontSize: '11px',
    fontWeight: 700,
    },
    
    optionCardList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    },
    
    optionCard: {
        minHeight: '42px',
        padding: '8px 10px',
        border: '0px solid #ddd',
        borderRadius: '6px',
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '13px',
        fontWeight: 500
    },
    
    optionCardSelected: {
    border: '1px solid #0073e9',
    background: '#f7fbff',
    color: '#0073e9',
    fontWeight: 700,
    },
    
    optionExtraPrice: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#0073e9',
    },

    clearOptionBtn: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        background: 'transparent',
        border: 'none',
        color: '#999',
        cursor: 'pointer',
        padding: 0
    },
};

export default ProductDetail;